<template>
  <div class="season-pass-tier-row">
    <div class="tile-bg" :style="tileStyle"></div>

    <div v-if="isLocked" class="lock-overlay" :style="lockOverlayStyle"></div>

    <div class="number">{{ levelNumber }}</div>

    <div class="progress-container">
      <div class="progress-frame" :style="progressFrameStyle"></div>
      <div class="progress-bar" :style="progressBarStyle">
        <div
          class="progress-fill"
          :style="{ width: progressWidth + '%' }"
        ></div>
      </div>
      <div class="progress-percent-badge" :style="progressBadgeStyle">
        {{ Math.floor(progressWidth) }}%
      </div>
    </div>

    <div class="reward-row premium">
      <BaseReward
        v-for="reward in premiumRewards"
        :key="`premium-${reward.id || (reward.asset && reward.asset.title)}`"
        :reward-data="reward"
        :shorten-gold-amount="true"
        :is-kingspass="true"
      />
      <div v-if="isPremium && isLevelCompleted" class="check-icon"></div>
      <div v-else-if="!isPremium" class="lock-icon"></div>
    </div>

    <div class="reward-row free">
      <BaseReward
        v-for="reward in normalRewards"
        :key="`normal-${reward.id || (reward.asset && reward.asset.title)}`"
        :reward-data="reward"
        :shorten-gold-amount="true"
      />
      <div v-if="isLevelCompleted" class="check-icon"></div>
    </div>
  </div>
</template>

<script>
import BaseReward from "@/components/base/base-reward.vue";

export default {
  name: "SeasonPassTierRow",
  components: { BaseReward },
  props: {
    level: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    pass: {
      type: Object,
      required: true,
    },
    rewards: {
      type: Object,
      default: () => ({}),
    },
    isCurrent: {
      type: Boolean,
      default: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    isCompleted: {
      type: Boolean,
      default: false,
    },
    userProgress: {
      type: Number,
      default: null,
    },
    isPremium: {
      type: Boolean,
      default: false,
    },
    ticketsEnabled: {
      type: Boolean,
      default: false,
    },
    currentProgress: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    passDisplayConfig() {
      return this.parseConfig(
        this.pass.display_config || this.pass.displayConfig
      );
    },
    levelDisplayConfig() {
      return this.parseConfig(
        this.level.display_config || this.level.displayConfig
      );
    },
    tileUrl() {
      return (
        this.getMediaUrl(this.level, "tile_unlocked") ||
        this.getMediaUrl(this.pass, "tile_unlocked") ||
        this.getConfigUrl(this.levelDisplayConfig, "tile_unlocked") ||
        this.getConfigUrl(this.passDisplayConfig, "tile_unlocked") ||
        this.getConfigUrl(this.levelDisplayConfig, "tile") ||
        this.getConfigUrl(this.passDisplayConfig, "tile") ||
        null
      );
    },
    tileStyle() {
      return this.tileUrl ? { backgroundImage: `url("${this.tileUrl}")` } : {};
    },
    lockOverlayUrl() {
      return (
        this.getMediaUrl(this.level, "tile_locked") ||
        this.getMediaUrl(this.pass, "tile_locked") ||
        this.getConfigUrl(this.levelDisplayConfig, "tile_locked") ||
        this.getConfigUrl(this.passDisplayConfig, "tile_locked") ||
        this.getMediaUrl(this.level, "tile_lock_overlay") ||
        this.getMediaUrl(this.pass, "tile_lock_overlay") ||
        this.getConfigUrl(this.levelDisplayConfig, "tileLockOverlay") ||
        this.getConfigUrl(this.levelDisplayConfig, "tile_lock_overlay") ||
        this.getConfigUrl(this.passDisplayConfig, "tileLockOverlay") ||
        this.getConfigUrl(this.passDisplayConfig, "tile_lock_overlay") ||
        null
      );
    },
    lockOverlayStyle() {
      return this.lockOverlayUrl
        ? { backgroundImage: `url("${this.lockOverlayUrl}")` }
        : {};
    },
    progressFrameUrl() {
      return (
        this.getMediaUrl(this.pass, "progress_frame") ||
        this.getConfigUrl(this.passDisplayConfig, "progressFrame") ||
        this.getConfigUrl(this.passDisplayConfig, "progress_frame") ||
        require("@/assets/img/events/winter-bonus-jagd/progress-top-frame.png")
      );
    },
    progressFrameStyle() {
      return { backgroundImage: `url("${this.progressFrameUrl}")` };
    },
    progressBarUrl() {
      return (
        this.getMediaUrl(this.pass, "progress_bar") ||
        this.getConfigUrl(this.passDisplayConfig, "progressBar") ||
        this.getConfigUrl(this.passDisplayConfig, "progress_bar") ||
        require("@/assets/img/events/winter-bonus-jagd/progress-bottom-bg.png")
      );
    },
    progressBarStyle() {
      return { backgroundImage: `url("${this.progressBarUrl}")` };
    },
    progressBadgeUrl() {
      return (
        this.getMediaUrl(this.pass, "progress_badge") ||
        this.getConfigUrl(this.passDisplayConfig, "progressBadge") ||
        this.getConfigUrl(this.passDisplayConfig, "progress_badge") ||
        require("@/assets/img/events/winter-bonus-jagd/progress-bg-percent.png")
      );
    },
    progressBadgeStyle() {
      return { backgroundImage: `url("${this.progressBadgeUrl}")` };
    },
    progressWidth() {
      if (this.isCompleted) return 100;
      if (this.isLocked) return 0;
      if (this.userProgress !== null) return this.userProgress;
      return 0;
    },
    isLevelCompleted() {
      return (
        this.isCompleted || (this.isCurrent && (this.userProgress || 0) >= 100)
      );
    },
    levelNumber() {
      return (this.level && this.level.level) || this.index + 1;
    },
    rewardOrder() {
      return ["card", "expbooster", "gold", "elixir", "ticket"];
    },
    premiumRewards() {
      return this.buildDisplayRewards(this.extractRewards(1));
    },
    normalRewards() {
      return this.buildDisplayRewards(this.extractRewards(0));
    },
    tierVisualState() {
      return {
        level: this.index + 1,
        backendLevelNumber: this.level && this.level.level,
        levelId: this.level && this.level.id,
        backendLevel: this.level,
        backendRewardsByTier: this.rewards,
        backendPass: this.pass,
        backendPassTiers: (this.pass && this.pass.tiers) || [],
        isCurrent: this.isCurrent,
        isLocked: this.isLocked,
        isCompleted: this.isCompleted,
        isLevelCompleted: this.isLevelCompleted,
        isPremium: this.isPremium,
        userProgress: this.userProgress,
        progressWidth: this.progressWidth,
        baseTileUrl: this.tileUrl,
        lockedOverlayUrl: this.lockOverlayUrl,
        levelTileLockedUrl: this.getMediaUrl(this.level, "tile_locked"),
        levelTileUnlockedUrl: this.getMediaUrl(this.level, "tile_unlocked"),
        passTileLockedUrl: this.getMediaUrl(this.pass, "tile_locked"),
        passTileUnlockedUrl: this.getMediaUrl(this.pass, "tile_unlocked"),
      };
    },
  },
  watch: {
    tierVisualState: {
      handler() {
        this.logTierVisualState();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    logTierVisualState() {
      // console.log("[seasonPass] tier row visual state", this.tierVisualState);
    },
    parseConfig(raw) {
      if (!raw) return {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (e) {
          return {};
        }
      }
      return raw;
    },
    getMediaUrl(source, key) {
      const media = source && source[key];
      if (Array.isArray(media)) return (media[0] && media[0].url) || "";
      if (media && typeof media === "object") return media.url || "";
      if (Array.isArray(source && source.media)) {
        const match = source.media.find((item) =>
          [
            item.collection_name,
            item.collectionName,
            item.name,
            item.key,
            item.type,
          ].includes(key)
        );
        return (match && (match.url || match.original_url)) || "";
      }
      return typeof media === "string" ? media : "";
    },
    getConfigUrl(cfg, key) {
      return (
        cfg[key] ||
        cfg[`${key}Url`] ||
        cfg[`${key}_url`] ||
        cfg[`${key}Path`] ||
        cfg[`${key}_path`] ||
        cfg[`${key}PathFull`] ||
        cfg[`${key}_path_full`] ||
        ""
      );
    },
    rewardTypeFor(reward) {
      if (!reward) return "";
      if (reward.customReward && reward.type) {
        return String(reward.type).toLowerCase();
      }

      const asset = reward.asset || {};
      const payloadType = asset.payload && asset.payload.type;
      if (payloadType) {
        return String(payloadType).toLowerCase();
      }

      if (asset.type && String(asset.type).endsWith("Card")) {
        return "card";
      }

      if (asset.title && this.rewardOrder.includes(asset.title)) {
        return String(asset.title).toLowerCase();
      }

      return "gold";
    },
    buildDisplayRewards(rewards) {
      const actualRewards = Array.isArray(rewards)
        ? rewards.filter(Boolean)
        : [];
      return this.sortRewards(actualRewards);
    },
    extractRewards(track) {
      // rewards is shaped like { 0: [...normal], 1: [...kingspass] } or array
      if (!this.rewards) return [];
      if (Array.isArray(this.rewards)) {
        return this.rewards[track] || [];
      }
      const values = Object.values(this.rewards);
      return values[track] || [];
    },
    sortRewards(arr) {
      if (!Array.isArray(arr)) return [];
      return [...arr].sort((a, b) => {
        const ai = this.rewardOrder.indexOf(this.rewardTypeFor(a));
        const bi = this.rewardOrder.indexOf(this.rewardTypeFor(b));
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.season-pass-tier-row {
  position: relative;
  width: 360px;
  height: 405px;
  display: inline-grid;

  .tile-bg {
    position: absolute;
    inset: 0;
    width: 404px;
    height: 405px;
    background-position: bottom center;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 1;
  }

  .lock-overlay {
    position: absolute;
    inset: 0;
    width: 404px;
    height: 405px;
    top: 0px;
    background-position: bottom center;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 21;
    pointer-events: none;
  }

  .number {
    position: absolute;
    font-size: 26px;
    top: 20px;
    left: 202px;
    transform: translateX(-50%);
    z-index: 5;
  }

  .progress-container {
    position: absolute;
    width: 140px;
    height: 31px;
    top: 80px;
    left: 202px;
    transform: translateX(-50%);
    z-index: 5;

    .progress-frame {
      position: absolute;
      top: 0;
      left: 0;
      width: 103px;
      height: 23px;
      z-index: 5;
      background-position: left;
      background-size: 100% 100%;
      background-repeat: no-repeat;
    }

    .progress-bar {
      width: 95px;
      height: 23px;
      overflow: hidden;
      outline: 10px solid transparent;
      border-radius: 16px;
      border: double 2px transparent;
      background-position: left;
      background-size: 100% 100%;
      background-repeat: no-repeat;

      .progress-fill {
        height: 100%;
        max-width: 100%;
        border-top-left-radius: 16px;
        border-bottom-left-radius: 16px;
        background-color: #2ea304;
      }
    }

    .progress-percent-badge {
      position: absolute;
      right: 0;
      top: -5px;
      z-index: 6;
      padding: 3px 0 0 7px;
      width: 54px;
      height: 34px;
      background-position: left;
      background-size: 100% 100%;
      background-repeat: no-repeat;
    }
  }

  .reward-row {
    position: absolute;
    display: inline-flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 1rem;
    z-index: 5;

    .check-icon {
      position: absolute;
      width: 48px;
      height: 48px;
      background: url(~@/assets/img/events/winter-bonus-jagd/green-checkmark-circle.png)
        right/100% 100% no-repeat;
    }

    .lock-icon {
      position: absolute;
      width: 44px;
      height: 59px;
      background: url(~@/assets/img/events/winter-bonus-jagd/gold-lock.png)
        right/100% 100% no-repeat;
    }

    &.premium {
      top: 130px;
      left: 55%;
      transform: translateX(-50%);
      width: 300px;

      .check-icon {
        right: -30px;
        bottom: -32px;
      }
      .lock-icon {
        right: -20px;
        bottom: -32px;
      }
    }

    &.free {
      top: 260px;
      left: 55%;
      transform: translateX(-50%);
      width: 200px;

      .check-icon {
        right: -20px;
        top: 50px;
      }
    }
  }
}
</style>
